import { Input, RadioGroupRadioButton } from "@light-technology/components";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { api } from "../state/api";
import { Role } from "../state/api.types";
import { useAuthHasRole } from "../state/hooks";
import { useTypedSelector } from "../state/store";
import { toSlug } from "../utilities/slug";
import { FormModal } from "./shared/FormModal";

type CreateAppForm = {
  name: string;
};

type CreateAppModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const CreateAppModal = (props: CreateAppModalProps) => {
  const navigate = useNavigate();

  const { orgSlug } = useTypedSelector((state) => state.local);
  const [createApp, createAppResult] = api.useCreateAppMutation();
  const { handleSubmit, reset, control } = useForm<CreateAppForm>({
    defaultValues: { name: "" },
  });
  const hasPermission = useAuthHasRole(Role.Developer, Role.OrgAdmin);

  const onSubmit = handleSubmit(({ name }) => {
    const slug = toSlug(name);
    createApp({ orgSlug: orgSlug!, name, slug }).then(() => {
      props.setIsOpen(false);
      navigate(`/apps/${slug}${hasPermission ? "" : "/accounts"}`);
    });
  });

  useEffect(() => {
    if (props.isOpen) {
      reset({ name: "" });
    }
  }, [props.isOpen, reset]);

  return (
    <FormModal
      isOpen={props.isOpen}
      onOpenChange={props.setIsOpen}
      textTitle="What should your new app be called?"
      textCancel="Cancel"
      textSubmit="Create app"
      isLoading={createAppResult.isLoading}
      onCancel={() => {
        props.setIsOpen(false);
      }}
      onSubmit={onSubmit}
      data-ph-capture-attribute-action="create-app"
    >
      <Controller
        control={control}
        name="name"
        rules={{ required: "Name is required" }}
        render={({ field, fieldState }) => (
          <>
            <Input
              {...field}
              aria-label="App name"
              type="text"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
            <div className="mt-sm text-sm text-tertiary">
              Slug preview: {toSlug(field.value)}
            </div>
          </>
        )}
      />
      <div className="mt-2xl">
        <div className="mb-md text-sm font-medium text-secondary">
          Environment
        </div>
        <RadioGroupRadioButton
          isDisabled
          defaultValue="sandbox"
          aria-label="Environment"
          items={
            [
              {
                value: "sandbox",
                title: "Sandbox",
                description:
                  "Safely test an app without activating electricity meters or charging payment methods",
              },
              {
                value: "live",
                title: "Live",
                description:
                  "Enroll real customers into electricity meter activations and billing",
              },
            ] as Parameters<typeof RadioGroupRadioButton>[0]["items"]
          }
        />
      </div>
      <div className="mt-2xl text-sm font-semibold text-tertiary">
        How do I create a live app?
      </div>
      <div className="text-sm text-tertiary">
        Reach out to use and we can get you going with a new live app.{" "}
        <a className="underline">Email us</a>
      </div>
    </FormModal>
  );
};
